import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import './AddEditIntent.scss';
import { translateThis } from '../../../helpers/language.helper';
import { showSnackbar } from '../../../components/snackbar/index';
import ApiCaller from '../../../services/api/general';

const AddEditIntent = ({ isOpen, setIsOpen, refresh, editId = '' }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [modules, setModules] = useState([]);

    useEffect(() => {
        if (isOpen) {
            const apiCaller = new ApiCaller('modules');
            apiCaller.getList({ limit: 1000, page: 1 }).then((res) => {
                const moduleList = res?.data?.results || res?.results || [];
                setModules(moduleList);
            }).catch(() => {
                showSnackbar().failure("Failed to fetch modules");
            });

            if (editId) {
                setIsLoading(true);
                const intentCaller = new ApiCaller(`intents`);
                intentCaller.getData(editId).then((res) => {
                    const data = res?.data || res;
                    if (data) {
                        formik.setValues({
                            name: data.name || '',
                            displayName: data.displayName || '',
                            keywords: data.keywords?.join(', ') || '',
                            promptOverride: data.promptOverride || '',
                            status: data.status || 'active',
                            priority: data.priority || '',
                            scope: data.scope || 'module',
                            moduleId: data.moduleId || '',
                        });
                    }
                }).catch(() => {
                    showSnackbar().failure("Failed to fetch intent details");
                }).finally(() => {
                    setIsLoading(false);
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, editId]);

    const validationSchema = Yup.object({
        name: Yup.string().required('Name is required'),
        displayName: Yup.string().required('Display Name is required'),
        keywords: Yup.string().required('Keywords are required'),
        status: Yup.string().required('Status is required'),
        priority: Yup.number().required('Priority is required'),
        scope: Yup.string().required('Scope is required'),
        moduleId: Yup.string().when('scope', {
            is: 'module',
            then: (schema) => schema.required('Module is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            displayName: '',
            keywords: '',
            promptOverride: '',
            status: 'active',
            priority: '',
            scope: 'module',
            moduleId: '',
        },
        validationSchema,
        onSubmit: (values) => {
            setIsLoading(true);
            const apiCaller = new ApiCaller('intents');

            const postData = {
                ...values,
                keywords: values.keywords.split(',').map(item => item.trim()).filter(item => item !== ''),
            };

            if (values.scope === 'global') {
                delete postData.moduleId;
            }

            const apiCall = editId ? apiCaller.customPutData(editId, postData) : apiCaller.postData(postData);

            apiCall.then((data) => {
                if (data.success) {
                    showSnackbar().success(editId ? "Intent updated successfully" : "Intent created successfully");
                    setIsOpen(false);
                    formik.resetForm();
                    refresh();
                } else {
                    showSnackbar().failure(data.message || "Failed to process intent");
                }
            }).finally(() => {
                setIsLoading(false);
            });
        },
    });

    const handleCancel = () => {
        formik.resetForm();
        setIsOpen(false);
    };

    if (!isOpen) return null;

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className="admin-modal-overlay" onClick={(e) => e.target === e.currentTarget && handleCancel()}>
            <div className="admin-modal">
                <div className="modal-header">
                    <div className="header-left">
                        <h2 className="modal-title">{editId ? 'Edit Intent' : 'Create New Intent'}</h2>
                    </div>
                    <button type="button" className="close-button" onClick={handleCancel}>
                        <X />
                    </button>
                </div>

                <div className="modal-body">
                    {isLoading && editId && !formik.values.name ? (
                        <div className="modal-loader">
                            <div className="spinner" />
                            <p>Loading intent details...</p>
                        </div>
                    ) : (
                        <form onSubmit={formik.handleSubmit}>
                            <div className="form-grid">
                                <div className="form-field">
                                    <div className="label">Name</div>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formik.values.name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className={`form-input ${formik.touched.name && formik.errors.name ? 'error' : ''}`}
                                        placeholder="Enter intent name"
                                    />
                                    {formik.touched.name && formik.errors.name && (
                                        <div className="error-message">{formik.errors.name}</div>
                                    )}
                                </div>

                                <div className="form-field">
                                    <div className="label">Display Name</div>
                                    <input
                                        type="text"
                                        name="displayName"
                                        value={formik.values.displayName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className={`form-input ${formik.touched.displayName && formik.errors.displayName ? 'error' : ''}`}
                                        placeholder="Enter display name"
                                    />
                                    {formik.touched.displayName && formik.errors.displayName && (
                                        <div className="error-message">{formik.errors.displayName}</div>
                                    )}
                                </div>

                                <div className="form-field full-width">
                                    <div className="label">Keywords (comma separated)</div>
                                    <input
                                        type="text"
                                        name="keywords"
                                        value={formik.values.keywords}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className={`form-input ${formik.touched.keywords && formik.errors.keywords ? 'error' : ''}`}
                                        placeholder="test, demo, example"
                                    />
                                    {formik.touched.keywords && formik.errors.keywords && (
                                        <div className="error-message">{formik.errors.keywords}</div>
                                    )}
                                </div>

                                <div className="form-field full-width">
                                    <div className="label">Prompt Override</div>
                                    <textarea
                                        name="promptOverride"
                                        value={formik.values.promptOverride}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className="form-input"
                                        style={{ height: '100px', resize: 'vertical' }}
                                        placeholder="Enter prompt override"
                                    />
                                </div>

                                <div className="form-field">
                                    <div className="label">Priority</div>
                                    <input
                                        type="number"
                                        name="priority"
                                        value={formik.values.priority}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className={`form-input ${formik.touched.priority && formik.errors.priority ? 'error' : ''}`}
                                    />
                                    {formik.touched.priority && formik.errors.priority && (
                                        <div className="error-message">{formik.errors.priority}</div>
                                    )}
                                </div>

                                <div className="form-field">
                                    <div className="label">Status</div>
                                    <div className="form-radio-group">
                                        <label className="radio-item" htmlFor="status-active">
                                            <input
                                                type="radio"
                                                id="status-active"
                                                name="status"
                                                value="active"
                                                checked={formik.values.status === 'active'}
                                                onChange={formik.handleChange}
                                            />
                                            Active
                                        </label>
                                        <label className="radio-item" htmlFor="status-inactive">
                                            <input
                                                type="radio"
                                                id="status-inactive"
                                                name="status"
                                                value="inactive"
                                                checked={formik.values.status === 'inactive'}
                                                onChange={formik.handleChange}
                                            />
                                            Inactive
                                        </label>
                                    </div>
                                </div>

                                <div className="form-field">
                                    <div className="label">Scope</div>
                                    <div className="form-radio-group">
                                        <label className="radio-item" htmlFor="scope-module">
                                            <input
                                                type="radio"
                                                id="scope-module"
                                                name="scope"
                                                value="module"
                                                checked={formik.values.scope === 'module'}
                                                onChange={formik.handleChange}
                                            />
                                            Module
                                        </label>
                                        <label className="radio-item" htmlFor="scope-global">
                                            <input
                                                type="radio"
                                                id="scope-global"
                                                name="scope"
                                                value="global"
                                                checked={formik.values.scope === 'global'}
                                                onChange={formik.handleChange}
                                            />
                                            Global
                                        </label>
                                    </div>
                                </div>

                                {formik.values.scope === 'module' && (
                                    <div className="form-field">
                                        <div className="label">Module</div>
                                        <select
                                            name="moduleId"
                                            value={formik.values.moduleId}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            className={`form-input ${formik.touched.moduleId && formik.errors.moduleId ? 'error' : ''}`}
                                        >
                                            <option value="">Select a module</option>
                                            {modules.map((m) => (
                                                <option key={m.id || m._id} value={m.id || m._id}>
                                                    {m.title || m.name}
                                                </option>
                                            ))}
                                        </select>
                                        {formik.touched.moduleId && formik.errors.moduleId && (
                                            <div className="error-message">{formik.errors.moduleId}</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="action-buttons">
                                <button type="button" onClick={handleCancel} className="btn btn-secondary">
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                    {isLoading ? (editId ? 'Updating...' : 'Creating...') : (editId ? 'Update' : 'Create')}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddEditIntent;
